import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  UserCredential,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, app } from '../firebase/config';
import { User, LoginCredentials, RegisterCredentials, UserRole } from '../types/auth';
import { Organization, MembershipRequest, Invitation } from '../types/organization';
import { SecurityUtils } from '../utils/securityUtils';
import { CaptchaService } from './captchaService';
import { 
  RegisterCredentialsSchema, 
  LoginCredentialsSchema, 
  UpdateProfileSchema,
  ValidatedRegisterCredentials
} from '../schemas/user';

export type AuthErrorContext = 'login' | 'register' | 'reset-password' | 'update-profile' | 'logout' | 'general';

export class AuthService {
  static async login(credentials: LoginCredentials): Promise<UserCredential> {
    try {
      // Validate credentials with Zod schema
      const validatedCredentials = LoginCredentialsSchema.parse(credentials);
      
      const userCredential = await signInWithEmailAndPassword(
        auth,
        validatedCredentials.email,
        validatedCredentials.password
      );
      
      // Update last login time
      await this.updateLastLogin(userCredential.user.uid);
      
      return userCredential;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthService.login: Error occurred:', error instanceof Error ? error.message : 'Unknown error');
      }
      throw this.handleAuthError(error, 'login');
    }
  }

  static async register(credentials: RegisterCredentials, invitationToken?: string): Promise<UserCredential> {
    // Validate credentials with Zod schema
    const validatedCredentials = RegisterCredentialsSchema.parse(credentials);

    // Sanitize display name
    validatedCredentials.displayName = SecurityUtils.sanitizeInput(validatedCredentials.displayName);

    // Bot prevention: CAPTCHA verification for public registrations (not required for invited accounts)
    if (!invitationToken) {
      // Unconditionally reject RFC 2606 example domains across all environments
      if (CaptchaService.isReservedExampleEmail(validatedCredentials.email)) {
        throw new Error('Registration with @example email addresses is not permitted. Please use a valid email address.');
      }

      if (process.env.NODE_ENV !== 'test') {
        if (!credentials.captchaToken) {
          throw new Error('CAPTCHA verification is required for account creation.');
        }
        const isCaptchaValid = await CaptchaService.verifyToken(credentials.captchaToken);
        if (!isCaptchaValid) {
          throw new Error('CAPTCHA verification failed or has expired. Please complete the verification again.');
        }
      } else if (credentials.captchaToken) {
        const isCaptchaValid = await CaptchaService.verifyToken(credentials.captchaToken);
        if (!isCaptchaValid) {
          throw new Error('CAPTCHA verification failed or has expired. Please complete the verification again.');
        }
      }
    }

    try {

      let userCredential: UserCredential;
      
      try {
        // Try to create a new user
        userCredential = await createUserWithEmailAndPassword(
          auth,
          validatedCredentials.email,
          validatedCredentials.password
        );
        
        // Update profile
        await updateProfile(userCredential.user, {
          displayName: validatedCredentials.displayName,
        });

        // Send email verification
        await sendEmailVerification(userCredential.user);
        
      } catch (createError: unknown) {
        // If user already exists and we're in invitation flow, try to sign them in
        const errorCode = (createError as { code?: string }).code;
        if (errorCode === 'auth/email-already-in-use' && invitationToken) {
          if (process.env.NODE_ENV === 'development') {
            console.log('AuthService.register: User already exists, attempting sign-in for invitation flow');
          }
          
          try {
            // Try to sign in with the provided password
            userCredential = await signInWithEmailAndPassword(
              auth,
              validatedCredentials.email,
              validatedCredentials.password
            );
            
            // Update profile if display name is different
            if (userCredential.user.displayName !== validatedCredentials.displayName) {
              await updateProfile(userCredential.user, {
                displayName: validatedCredentials.displayName,
              });
            }
            
          } catch (signInError: unknown) {
            const signInErrorCode = (signInError as { code?: string }).code;
            if (process.env.NODE_ENV === 'development') {
              console.log('AuthService.register: Sign-in failed for existing user:', signInErrorCode);
            }
            
            // If sign-in fails due to wrong password, we need to handle this case
            if (signInErrorCode === 'auth/wrong-password' || signInErrorCode === 'auth/invalid-credential' || signInErrorCode === 'auth/invalid-login-credentials') {
              throw new Error('A user with this email already exists. If you forgot your password, please use the password reset option on the login page.');
            } else {
              throw new Error('A user with this email already exists but there was an authentication error. Please contact support.');
            }
          }
        } else {
          // Re-throw the original error if not an invitation flow or different error
          throw createError;
        }
      }

      // Create user document in Firestore (this will handle both new and existing users)
      await this.createUserDocument(userCredential.user, validatedCredentials, invitationToken, validatedCredentials.organizationName);

      return userCredential;
    } catch (error) {
      throw this.handleAuthError(error, 'register');
    }
  }

  

  static async validateInvitation(tokenOrId: string): Promise<Invitation> {
    let invitationDoc;

    // Try treating tokenOrId as the document ID first (this uses 'get' which is allowed for pending invites)
    try {
      const docRef = doc(db, 'invitations', tokenOrId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists() && docSnap.data().status === 'pending') {
        invitationDoc = docSnap;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('validateInvitation: getDoc failed', error);
      }
    }

    // Fallback: Try treating tokenOrId as a token field (this uses 'list' which requires strict email matching or admin rights)
    if (!invitationDoc) {
      try {
        const invitationQuery = query(
          collection(db, 'invitations'),
          where('token', '==', tokenOrId),
          where('status', '==', 'pending')
        );
        const invitationSnapshot = await getDocs(invitationQuery);
        
        if (!invitationSnapshot.empty) {
          invitationDoc = invitationSnapshot.docs[0];
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.log('validateInvitation: getDocs failed (likely due to list permissions)', error);
        }
      }
    }

    if (!invitationDoc) {
      throw new Error('Invalid or expired invitation');
    }
    
    const data = invitationDoc.data();
    
    // Check if invitation is expired
    const now = new Date();
    const expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
    
    if (now > expiresAt) {
      // Mark as expired
      await updateDoc(doc(db, 'invitations', invitationDoc.id), {
        status: 'expired',
        updatedAt: serverTimestamp()
      });
      throw new Error('Invitation has expired');
    }
    
    const invitation: Invitation = {
      id: invitationDoc.id,
      organizationId: data.organizationId,
      organizationName: data.organizationName,
      email: data.email,
      displayName: data.displayName,
      role: data.role,
      invitedBy: data.invitedBy,
      invitedByName: data.invitedByName,
      token: data.token,
      status: data.status,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      expiresAt: expiresAt,
    };
    
    return invitation;
  }

  static async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      throw this.handleAuthError(error, 'logout');
    }
  }

  static async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw this.handleAuthError(error, 'reset-password');
    }
  }

  static async createUserDocument(
    firebaseUser: FirebaseUser,
    credentials: RegisterCredentials | ValidatedRegisterCredentials,
    invitationToken?: string,
    organizationName?: string
  ): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthService.createUserDocument: Starting user creation:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        hasInvitationToken: !!invitationToken
      });
    }
    
    let organizationId: string;
    let userRole: UserRole = 'user';
    let invitationIdToAccept: string | undefined;
    
    if (invitationToken) {
      // Handle invitation-based signup
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthService.createUserDocument: Validating invitation token');
      }
      
      const invitation = await this.validateInvitation(invitationToken);
      organizationId = invitation.organizationId;
      userRole = invitation.role;
      invitationIdToAccept = invitation.id;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthService.createUserDocument: Invitation validated:', {
          organizationId,
          userRole,
          invitationEmail: invitation.email
        });
      }
    } else {
      // Check if organization exists or create new one
      const orgResult = await this.findOrCreateOrganization(
        organizationName || credentials.organizationName,
        firebaseUser.uid,
        firebaseUser.email || credentials.email,
        credentials.displayName
      );
      organizationId = orgResult.organizationId;
      
      if (orgResult.isPending) {
        // Create user document without organization (they'll be added when approved)
        const userDoc: Omit<User, 'id'> = {
          email: firebaseUser.email!,
          displayName: credentials.displayName,
          ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL }),
          role: 'user' as UserRole,
          organizationIds: [], // Empty until approved
          createdAt: new Date(),
          updatedAt: new Date(),
          isActive: true,
          lastLoginAt: new Date(),
          preferences: {
            theme: 'light',
            notifications: {
              email: true,
              push: true,
              taskAssignment: true,
              assignmentHeadsUp: true,
            },
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: 'en',
          },
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), {
          ...userDoc,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        }, { merge: true });
        
        // Throw special error to indicate pending approval
        throw new Error('MEMBERSHIP_REQUEST_PENDING');
      } else {
        // Creator of a new organization is the account owner
        userRole = 'account_owner';
      }
    }

    const userDoc: Omit<User, 'id'> = {
      email: firebaseUser.email!,
      displayName: credentials.displayName,
      ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL }),
      role: userRole,
      organizationIds: [organizationId],
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      lastLoginAt: new Date(),
      preferences: {
        theme: 'light',
        notifications: {
          email: true,
          push: true,
          taskAssignment: true,
          assignmentHeadsUp: true,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: 'en',
      },
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('AuthService.createUserDocument: Creating user document:', {
        uid: firebaseUser.uid,
        email: userDoc.email,
        organizationIds: userDoc.organizationIds,
        role: userDoc.role
      });
    }

    // Step 1: Create the user document in Firestore FIRST so users/{userId} exists for Cloud Functions
    await setDoc(doc(db, 'users', firebaseUser.uid), {
      ...userDoc,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }, { merge: true });

    // Step 2: Accept invitation via Cloud Function if an invitation ID was provided
    if (invitationIdToAccept) {
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthService.createUserDocument: Accepting invitation via Cloud Function');
      }
      
      try {
        const functions = getFunctions(app, 'australia-southeast1');
        const acceptInvitationFn = httpsCallable<{ invitationId: string }, { success: boolean; organizationId: string }>(
          functions,
          'acceptInvitation'
        );
        
        const result = await acceptInvitationFn({ invitationId: invitationIdToAccept });
        
        if (!result.data.success) {
          throw new Error('Failed to accept invitation via Cloud Function');
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('AuthService.createUserDocument: Invitation accepted successfully via Cloud Function');
        }
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('AuthService.createUserDocument: Failed to accept invitation:', error);
        }
        const errorDetails = error?.message || (typeof error === 'string' ? error : 'Failed to accept invitation');
        throw new Error(`Failed to join organization via invitation: ${errorDetails}`);
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthService.createUserDocument: User document created successfully');
    }
  }

  static async findOrCreateOrganization(
    organizationName: string, 
    userId: string,
    userEmail?: string,
    userName?: string
  ): Promise<{organizationId: string, isPending: boolean}> {
    // Check if organization with this name already exists
    const orgQuery = query(
      collection(db, 'organizations'),
      where('name', '==', organizationName)
    );
    const orgSnapshot = await getDocs(orgQuery);
    
    if (!orgSnapshot.empty) {
      // Organization exists, create membership request
      const orgDoc = orgSnapshot.docs[0];
      const orgId = orgDoc.id;
      const orgData = orgDoc.data() as Organization;
      
      // Ensure members array exists
      if (!orgData.members) {
        orgData.members = [];
      }
      
      // Check if user is already a member
      const existingMember = orgData.members.find(member => member.userId === userId);
      if (existingMember) {
        return { organizationId: orgId, isPending: false };
      }
      
      // Check if there's already a pending request
      const existingRequestQuery = query(
        collection(db, 'membershipRequests'),
        where('organizationId', '==', orgId),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );
      const existingRequestSnapshot = await getDocs(existingRequestQuery);
      
      if (!existingRequestSnapshot.empty) {
        // Request already exists
        return { organizationId: orgId, isPending: true };
      }
      
      // Create new membership request
      const user = await this.getUserDocument(userId);
      const membershipRequest: Omit<MembershipRequest, 'id'> = {
        organizationId: orgId,
        organizationName: organizationName,
        userId: userId,
        userEmail: user?.email || userEmail || '',
        userName: user?.displayName || userName || '',
        requestedRole: 'user',
        status: 'pending',
        createdAt: new Date(),
      };
      
      await addDoc(collection(db, 'membershipRequests'), {
        ...membershipRequest,
        createdAt: serverTimestamp(),
      });
      
      return { organizationId: orgId, isPending: true };
    } else {
      // Create new organization
      const newOrg: Omit<Organization, 'id'> = {
        name: organizationName,
        ownerId: userId,
        members: [{
          userId,
          role: 'account_owner',
          joinedAt: new Date(),
          invitedBy: userId,
          isActive: true
        }],
        settings: {
          allowMemberInvites: true,
          defaultMemberRole: 'user',
          taskTemplateVisibility: 'organization',
          requireApprovalForTasks: false,
          maxTasksPerUser: 100,
          allowTemplateSharing: true,
          requireApprovalForNewMembers: true,
          billingTier: 'free',
          emailNotifications: true,
          notifyOnAssignment: true
        },
        subscription: {
          plan: 'free_trial',
          status: 'trial',
          startDate: new Date(),
          trialStartDate: new Date(),
          trialEndDate: (() => {
            const trialEnd = new Date();
            trialEnd.setDate(trialEnd.getDate() + 30);
            return trialEnd;
          })(),
          maxUsers: 10,
          maxTasks: 50,
          maxTemplates: 10,
          maxComponents: 20
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };
      
      const orgRef = await addDoc(collection(db, 'organizations'), {
        name: newOrg.name,
        ownerId: newOrg.ownerId,
        settings: newOrg.settings,
        subscription: newOrg.subscription,
        isActive: newOrg.isActive,
        currentUserCount: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        members: [{
          userId,
          role: 'account_owner',
          joinedAt: new Date(),
          invitedBy: userId,
          isActive: true
        }]
      });
      
      return { organizationId: orgRef.id, isPending: false };
    }
  }

  static async updateLastLogin(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthService.updateLastLogin: Error updating last login:', error instanceof Error ? error.message : 'Unknown error');
      }
      // Don't throw the error to prevent login failures
    }
  }

  private static parseTimestamp(val: any, fallback?: Date): Date | undefined {
    if (!val) return fallback;
    if (typeof val.toDate === 'function') {
      try {
        return val.toDate();
      } catch {
        return fallback;
      }
    }
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d;
    }
    return fallback;
  }

  static async getUserDocument(userId: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          id: userDoc.id,
          ...data,
          createdAt: this.parseTimestamp(data.createdAt, new Date())!,
          updatedAt: this.parseTimestamp(data.updatedAt, new Date())!,
          lastLoginAt: this.parseTimestamp(data.lastLoginAt),
        } as User;
      }
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching user document:', error instanceof Error ? error.message : 'Unknown error');
      }
      return null;
    }
  }

  static onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Firebase user authenticated:', firebaseUser.uid);
        }
        let userDoc = await this.getUserDocument(firebaseUser.uid);

        // Self-healing: repair incomplete invitation signups if user is missing organization or display name
        if (userDoc && firebaseUser.email && (!userDoc.organizationIds || userDoc.organizationIds.length === 0 || !userDoc.displayName)) {
          try {
            const pendingQuery = query(
              collection(db, 'invitations'),
              where('email', '==', firebaseUser.email),
              where('status', '==', 'pending')
            );
            const pendingSnap = await getDocs(pendingQuery);
            
            if (!pendingSnap.empty) {
              const inviteDoc = pendingSnap.docs[0];
              const inviteData = inviteDoc.data();
              
              const functions = getFunctions(app, 'australia-southeast1');
              const acceptInvitationFn = httpsCallable<{ invitationId: string }, { success: boolean; organizationId: string }>(
                functions,
                'acceptInvitation'
              );
              await acceptInvitationFn({ invitationId: inviteDoc.id });

              const repairData: Record<string, any> = {
                organizationIds: [inviteData.organizationId],
                role: inviteData.role || 'user',
                updatedAt: serverTimestamp(),
              };
              if (!userDoc.displayName) {
                repairData.displayName = inviteData.displayName || firebaseUser.displayName || 'User';
              }
              await updateDoc(doc(db, 'users', firebaseUser.uid), repairData);
              userDoc = await this.getUserDocument(firebaseUser.uid);
            } else if (!userDoc.displayName && firebaseUser.displayName) {
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                displayName: firebaseUser.displayName,
                updatedAt: serverTimestamp(),
              });
              userDoc = await this.getUserDocument(firebaseUser.uid);
            }
          } catch (repairErr) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Self-healing invitation repair error:', repairErr);
            }
          }
        }

        const allowDevGuest =
          typeof window !== 'undefined' &&
          process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true' &&
          process.env.REACT_APP_ALLOW_DEV_GUEST === 'true';

        if (!userDoc && allowDevGuest) {
          userDoc = await this.ensureLocalGuestUser(firebaseUser);
        }

        callback(userDoc);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Firebase user logged out');
        }
        callback(null);
      }
    });
  }

  private static async ensureLocalGuestUser(firebaseUser: FirebaseUser): Promise<User | null> {
    const allowDevGuest =
      typeof window !== 'undefined' &&
      process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true' &&
      process.env.REACT_APP_ALLOW_DEV_GUEST === 'true';

    if (!allowDevGuest || !firebaseUser.isAnonymous) {
      return null;
    }

    try {
      console.log('[ensureLocalGuestUser] seeding guest for', firebaseUser.uid);
      const orgId = `dev-guest-org-${firebaseUser.uid}`;
      const userId = firebaseUser.uid;
      const now = new Date();

      const orgRef = doc(db, 'organizations', orgId);
      const orgSnap = await getDoc(orgRef);

      const guestMember: Organization['members'][number] = {
        userId,
        role: 'admin' as UserRole,
        joinedAt: now,
        invitedBy: userId,
        isActive: true,
      };

      if (!orgSnap.exists()) {
        console.log('[ensureLocalGuestUser] creating guest org', orgId);
        await setDoc(orgRef, {
          name: 'Dev Guest Org',
          ownerId: userId,
          members: [guestMember],
          settings: {
            allowMemberInvites: true,
            defaultMemberRole: 'user',
            taskTemplateVisibility: 'organization',
            requireApprovalForTasks: false,
            maxTasksPerUser: 100,
            allowTemplateSharing: true,
            requireApprovalForNewMembers: true,
            billingTier: 'free',
            emailNotifications: true,
            notifyOnAssignment: true,
          },
          subscription: {
            plan: 'free_trial',
            status: 'trial',
            startDate: now,
            trialStartDate: now,
            trialEndDate: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000),
            maxUsers: 10,
            maxTasks: 50,
            maxTemplates: 10,
            maxComponents: 20,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
        });
      }

      const userRef = doc(db, 'users', userId);
      const displayName = firebaseUser.displayName || 'Local Guest';
      const email = firebaseUser.email || 'guest@localhost.local';

      console.log('[ensureLocalGuestUser] upserting guest user doc', userId);
      await setDoc(userRef, {
        email,
        displayName,
        role: 'admin',
        organizationIds: [orgId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        lastLoginAt: serverTimestamp(),
        preferences: {
          theme: 'light',
          notifications: {
            email: true,
            push: true,
            taskAssignment: true,
            assignmentHeadsUp: true,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: 'en',
        },
      }, { merge: true });

      console.log('[ensureLocalGuestUser] guest user seeded, fetching document');
      return await this.getUserDocument(userId);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('ensureLocalGuestUser failed:', error);
      }
      return null;
    }
  }

  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  static userHasPasswordProvider(): boolean {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    
    // Check if user has password provider
    return currentUser.providerData.some(provider => provider.providerId === 'password');
  }

  static async updateUserProfile(userId: string, updates: Partial<User>): Promise<void> {
    try {
      // Validate updates with Zod schema
      const validatedUpdates = UpdateProfileSchema.parse(updates);
      
      // Sanitize text inputs
      if (validatedUpdates.displayName) {
        validatedUpdates.displayName = SecurityUtils.sanitizeInput(validatedUpdates.displayName);
      }
      
      await updateDoc(doc(db, 'users', userId), {
        ...validatedUpdates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.handleAuthError(error, 'update-profile');
    }
  }

  static handleAuthError(error: unknown, context: AuthErrorContext = 'general'): Error {
    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error:', error instanceof Error ? error.message : 'Unknown error');
    }

    // Extract clean human-readable error message from ZodError or stringified Zod array
    if (error && typeof error === 'object') {
      const zErr = error as { name?: string; issues?: Array<{ message: string }>; errors?: Array<{ message: string }> };
      if (zErr.name === 'ZodError' || Array.isArray(zErr.issues) || Array.isArray(zErr.errors)) {
        const issues = zErr.issues || zErr.errors;
        if (issues && issues.length > 0 && issues[0]?.message) {
          return new Error(issues[0].message);
        }
      }
    }

    try {
      if (typeof (error as Error)?.message === 'string' && (error as Error).message.trim().startsWith('[')) {
        const parsed = JSON.parse((error as Error).message);
        if (Array.isArray(parsed) && parsed[0]?.message) {
          return new Error(parsed[0].message);
        }
      }
    } catch {
      // Ignore JSON parse error
    }

    // Extract error code from error.code or by parsing Firebase message string format: (auth/xxx)
    let errorCode = (error as { code?: string })?.code;
    const rawMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : '';

    if (!errorCode && rawMessage) {
      const match = rawMessage.match(/auth\/([a-z0-9-]+)/i);
      if (match) {
        errorCode = match[0].toLowerCase();
      }
    } else if (errorCode) {
      errorCode = errorCode.toLowerCase();
      if (!errorCode.startsWith('auth/') && !['google_signup_requires_organization', 'membership_request_pending'].includes(errorCode)) {
        errorCode = `auth/${errorCode}`;
      }
    }

    switch (errorCode) {
      // Login & Credential Errors
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
      case 'auth/wrong-password':
        return new Error('Invalid email or password.');

      case 'auth/user-not-found':
        if (context === 'reset-password') {
          return new Error('No account found with this email address.');
        }
        return new Error('Invalid email or password.');

      // Email formatting & validation
      case 'auth/invalid-email':
        return new Error('Invalid email address.');

      // Sign up / Registration
      case 'auth/email-already-in-use':
        return new Error('An account with this email address already exists. Please sign in instead.');

      case 'auth/weak-password':
        return new Error('Password is too weak. Please use at least 8 characters with lowercase, uppercase, and numbers.');

      case 'auth/user-disabled':
        return new Error('This account has been disabled. Please contact support.');

      case 'auth/operation-not-allowed':
        return new Error(
          context === 'register'
            ? 'Account registration is currently disabled. Please contact support.'
            : 'This sign-in method is currently disabled. Please contact support.'
        );

      // Throttling / Rate limiting
      case 'auth/too-many-requests':
        return new Error(
          context === 'login'
            ? 'Too many failed login attempts. Please try again later.'
            : 'Too many attempts. Please try again later.'
        );

      // Network & connectivity
      case 'auth/network-request-failed':
        return new Error('Network error. Please check your internet connection.');

      case 'auth/timeout':
        return new Error('The request timed out. Please try again.');

      case 'auth/quota-exceeded':
        return new Error('Service temporarily unavailable due to high traffic. Please try again later.');

      // Password Reset & Action Codes
      case 'auth/expired-action-code':
        return new Error('This link has expired. Please request a new one.');

      case 'auth/invalid-action-code':
        return new Error('This link is invalid or has already been used. Please request a new one.');

      // Re-authentication & sensitive actions
      case 'auth/requires-recent-login':
        return new Error('This action is sensitive and requires recent authentication. Please log in again.');

      // Missing credentials
      case 'auth/missing-password':
        return new Error('Password is required.');

      case 'auth/missing-email':
        return new Error('Email address is required.');

      // OAuth / Popup
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return new Error('Sign-in was cancelled before completion.');

      case 'auth/popup-blocked':
        return new Error('Sign-in popup was blocked by your browser. Please allow popups for this site.');

      case 'auth/account-exists-with-different-credential':
        return new Error('An account already exists with the same email address using a different sign-in method.');

      // Tokens & Sessions
      case 'auth/user-token-expired':
      case 'auth/id-token-expired':
        return new Error('Your session has expired. Please sign in again.');

      case 'auth/null-user':
        return new Error('No active user session found. Please sign in.');

      // Custom app codes
      case 'google_signup_requires_organization':
      case 'GOOGLE_SIGNUP_REQUIRES_ORGANIZATION':
        return new Error('Please complete your organization setup to continue.');

      case 'membership_request_pending':
      case 'MEMBERSHIP_REQUEST_PENDING':
        return new Error('Your request to join this organization is pending approval from an admin.');

      // Fallback
      default: {
        // Intercept any technical Firebase or internal error strings
        if (
          rawMessage.includes('Firebase:') ||
          rawMessage.includes('auth/') ||
          rawMessage.includes('FirebaseError') ||
          rawMessage.toLowerCase().includes('firebase')
        ) {
          if (context === 'login') {
            return new Error('Invalid email or password.');
          }
          if (context === 'register') {
            return new Error('Registration failed. Please check your details and try again.');
          }
          if (context === 'reset-password') {
            return new Error('Password reset failed. Please try again.');
          }
          return new Error('Authentication failed. Please try again.');
        }

        return new Error(rawMessage || 'An unexpected error occurred. Please try again.');
      }
    }
  }
}